import { useState, useRef } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const ChartCard = ({
  title,
  subheader,
  children,
  customActions = [],
  hideActions = false,
  hideDefaultActions = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [submenuItems, setSubmenuItems] = useState([]);
  const [loadingImage, setLoadingImage] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const contentRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (loadingImage || loadingPDF) {
      return;
    }
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
  };

  const handleSubmenuOpen = (event, items) => {
    setSubmenuAnchorEl(event.currentTarget);
    setSubmenuItems(items);
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchorEl(null);
    setSubmenuItems([]);
  };

  const handleSubmenuClick = (action) => {
    action.onClick();
    handleClose();
  };

  const handleExportImage = async () => {
    try {
      setLoadingImage(true);
      const scale = 10;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
      });

      const link = document.createElement("a");
      link.download = `${title}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } finally {
      setLoadingImage(false);
      setAnchorEl(null);
      setSubmenuAnchorEl(null);
    }
  };

  const handleExportPDF = async () => {
    try {
      setLoadingPDF(true);
      const scale = 10;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: "l",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = (pdfHeight - imgHeight * ratio) / 2;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
        "",
        "FAST"
      );
      pdf.save(`${title}.pdf`);
    } finally {
      setLoadingPDF(false);
      setAnchorEl(null);
      setSubmenuAnchorEl(null);
    }
  };

  const defaultActions = [
    {
      label: "Export as Image",
      onClick: handleExportImage,
      icon: <ImageIcon />,
      loading: loadingImage,
    },
    {
      label: "Export as PDF",
      onClick: handleExportPDF,
      icon: <PictureAsPdfIcon />,
      loading: loadingPDF,
    },
  ];

  const allActions = hideDefaultActions ? [...customActions] : [...defaultActions, ...customActions];

  return (
    <Card className="m-2">
      <CardHeader
        title={title}
        subheader={subheader}
        titleTypographyProps={{ variant: "h6", pl: 1 }}
        subheaderTypographyProps={{ variant: "body2", pl: 1 }}
        action={
          !hideActions && (
            <IconButton onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>
          )
        }
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {allActions.map((action, index) => (
          <div key={index}>
            {action.submenu ? (
              <MenuItem
                onClick={(e) => handleSubmenuOpen(e, action.submenu)}
                disabled={action.loading}
              >
                <ListItemIcon>
                  {action.loading ? <CircularProgress size={20} /> : action.icon}
                </ListItemIcon>
                <ListItemText>{action.label}</ListItemText>
                <ChevronRightIcon />
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  action.onClick();
                }}
                disabled={action.loading}
              >
                <ListItemIcon>
                  {action.loading ? <CircularProgress size={20} /> : action.icon}
                </ListItemIcon>
                <ListItemText>{action.label}</ListItemText>
              </MenuItem>
            )}
          </div>
        ))}
      </Menu>
      <Menu
        anchorEl={submenuAnchorEl}
        open={Boolean(submenuAnchorEl)}
        onClose={handleSubmenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {submenuItems.map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => handleSubmenuClick(item)}
            disabled={item.loading}
          >
            <ListItemIcon>
              {item.selected ? <CheckIcon /> : <div style={{ width: 24 }} />}
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <CardContent className="overflow-auto" ref={contentRef}>
        {children}
      </CardContent>
    </Card>
  );
};

export default ChartCard;
